import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useProfile } from '../../hooks/useProfile';
import { useOrganization } from '../../hooks/useOrganization';
import { RoleProtectedSkeleton } from './RoleProtectedSkeleton';
import { uiLogger } from '../../utils/loggerInstance';
import { Button } from '../ui/button';
import { AccountLoadErrorCard } from './AccountLoadErrorCard';

interface RoleProtectedRouteProps {
  allowedRoles: string[];
  redirectPath?: string;
  children?: React.ReactNode;
  loadingSkeleton?: React.ReactNode;
}

/**
 * Role-based route protection with non-blocking UX
 *
 * Shows the surrounding layout immediately and gates only the sensitive content
 * with a skeleton loader until the user's role is verified.
 *
 * Uses the same data source (organization from GET /organizations/me) as ProfileLayout
 * to ensure consistent role checking across the application.
 *
 * @param allowedRoles - Array of roles that can access this route (e.g., ['admin', 'owner'])
 * @param redirectPath - Path to redirect to if user doesn't have required role (default: '/profile')
 * @param children - Child components to render if authorized
 * @param loadingSkeleton - Custom skeleton component to show while loading (default: RoleProtectedSkeleton)
 *
 * @example
 * ```tsx
 * <RoleProtectedRoute allowedRoles={['admin', 'owner']}>
 *   <OrganizationSettings />
 * </RoleProtectedRoute>
 * ```
 */
export const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({
  allowedRoles,
  redirectPath = '/profile',
  children,
  loadingSkeleton,
}) => {
  const {
    data: userProfile,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useProfile();
  const {
    data: organization,
    isLoading: orgLoading,
    isError: orgError,
    refetch: refetchOrganization,
  } = useOrganization();

  const loading = profileLoading || orgLoading;

  // Show skeleton while loading
  if (loading) {
    return <>{loadingSkeleton || <RoleProtectedSkeleton />}</>;
  }

  // Profile query failed – show error so user isn't stuck on skeleton
  if (profileError) {
    return (
      <div className='flex flex-col items-center justify-center min-h-[40vh] p-4'>
        <div className='max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center'>
          <h3 className='text-lg font-medium text-gray-900 mb-2'>
            Couldn&apos;t load your profile
          </h3>
          <p className='text-sm text-gray-500 mb-6'>
            Something went wrong loading your account. Please try again.
          </p>
          <Button onClick={() => refetchProfile()} variant='default'>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (orgError) {
    return (
      <AccountLoadErrorCard
        onRetry={async () => {
          await refetchOrganization();
        }}
      />
    );
  }

  // No profile data yet (edge case)
  if (!userProfile) {
    return <>{loadingSkeleton || <RoleProtectedSkeleton />}</>;
  }

  // Get organization role from GET /organizations/me (same as ProfileLayout)
  const organizationRole = organization?.role ?? null;

  // Check if user has required role using the same logic as ProfileLayout
  const hasRequiredRole = organizationRole && allowedRoles.includes(organizationRole);

  if (!hasRequiredRole) {
    uiLogger.warn('Access denied: Insufficient role', {
      userRole: organizationRole,
      allowedRoles,
    });
    return <Navigate to={redirectPath} replace />;
  }

  // User is authorized - render protected content
  return children ? <>{children}</> : <Outlet />;
};
